/**
 * Phreatic Time Slider
 * Navigate through historical gauging dates.
 * Displays a slider over available dates with play/pause controls.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  PlayIcon,
  PauseIcon,
  BackwardIcon,
  ForwardIcon,
} from '@heroicons/react/24/solid';

interface PhreaticTimeSliderProps {
  dates: string[];
  selectedDate: string | null;
  onDateChange: (date: string) => void;
  className?: string;
  futureDates?: string[];
  /** Optional labels for future dates (e.g., "3mo", "1yr", "5yr") */
  futureLabels?: Record<string, string>;
}

export function PhreaticTimeSlider({ dates, selectedDate, onDateChange, className = '', futureDates, futureLabels }: PhreaticTimeSliderProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Combine historical + future dates
  const allDates = useMemo(() => {
    if (!futureDates || futureDates.length === 0) return dates;
    return [...dates, ...futureDates.filter(d => !dates.includes(d))];
  }, [dates, futureDates]);

  const todayDividerIndex = futureDates && futureDates.length > 0 ? dates.length - 1 : -1;
  const currentIndex = selectedDate ? allDates.indexOf(selectedDate) : allDates.length - 1;

  const goTo = useCallback((idx: number) => {
    const clamped = Math.max(0, Math.min(idx, allDates.length - 1));
    if (allDates[clamped]) {
      onDateChange(allDates[clamped]);
    }
  }, [allDates, onDateChange]);

  // Play/pause animation
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        goTo(currentIndex + 1);
      }, 1200);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, currentIndex, goTo]);

  // Stop at end
  useEffect(() => {
    if (isPlaying && currentIndex >= allDates.length - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, currentIndex, allDates.length]);

  if (allDates.length === 0) return null;

  const formatDate = (d: string) => {
    const dt = new Date(d + 'T00:00:00');
    return dt.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  return (
    <div className={`bg-mono-900 border border-mono-800 rounded-lg p-4 ${className}`}>
      <div className="flex items-center gap-4">
        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => goTo(currentIndex - 1)}
            disabled={currentIndex <= 0}
            className="p-1.5 rounded-md bg-mono-800 hover:bg-mono-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <BackwardIcon className="w-4 h-4 text-mono-300" />
          </button>
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="p-2 rounded-md bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/40 transition-colors"
          >
            {isPlaying ? (
              <PauseIcon className="w-4 h-4 text-cyan-300" />
            ) : (
              <PlayIcon className="w-4 h-4 text-cyan-300" />
            )}
          </button>
          <button
            onClick={() => goTo(currentIndex + 1)}
            disabled={currentIndex >= allDates.length - 1}
            className="p-1.5 rounded-md bg-mono-800 hover:bg-mono-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ForwardIcon className="w-4 h-4 text-mono-300" />
          </button>
        </div>

        {/* Slider */}
        <div className="flex-1">
          <input
            type="range"
            min={0}
            max={allDates.length - 1}
            value={currentIndex >= 0 ? currentIndex : allDates.length - 1}
            onChange={(e) => goTo(parseInt(e.target.value))}
            className="w-full h-1.5 bg-mono-700 rounded-lg appearance-none cursor-pointer accent-cyan-500"
          />
          <div className="flex justify-between mt-1">
            <span className="text-[10px] text-mono-500">
              {allDates.length > 0 ? formatDate(allDates[0]) : ''}
            </span>
            {todayDividerIndex >= 0 && (
              <span className="text-[10px] text-cyan-400 font-medium">TODAY</span>
            )}
            <span className="text-xs text-cyan-400 font-medium">
              {selectedDate ? formatDate(selectedDate) : (allDates.length > 0 ? formatDate(allDates[allDates.length - 1]) : '')}
            </span>
            <span className="text-[10px] text-mono-500">
              {allDates.length > 0 ? formatDate(allDates[allDates.length - 1]) : ''}
            </span>
          </div>
        </div>

        {/* Date count + future indicator */}
        <div className="text-xs text-mono-500 whitespace-nowrap">
          {currentIndex > todayDividerIndex && todayDividerIndex >= 0 ? (
            <span className="text-cyan-400">
              {futureLabels && selectedDate && futureLabels[selectedDate]
                ? `V5: ${futureLabels[selectedDate]}`
                : 'Predicted'}
            </span>
          ) : null}
          {' '}{currentIndex + 1} / {allDates.length}
        </div>
      </div>
    </div>
  );
}
